// "Take it" from a list: the ETA sheet, the take, the toast, and the way on to
// the thread for a handover — in one place, so the leader's Andon tab, the
// Overview's Needs-you cards and the Work centres cards all take an andon the
// same way (frame v2.12 script L820 `takeAndon`, L853, L866, L880: every
// "Take it" on the floor opens `{ kind: 'eta' }`).
//
// WHY A SHEET OVER THE LIST AND NOT THE THREAD. A leader clearing the floor
// takes three andons in three sheets rather than three threads; the take
// lands exactly as it does from the thread — one call, a time on the record,
// the operator told (L934: "The raiser sees your name and a countdown on
// their Home row"). What the list cannot do is hand the andon on, because
// the responder picker lives on the thread; so a handover from here is the
// take, then the thread.
//
// TWO SHAPES OF ANDON REACH THIS. The andon module's own `AndonAlertVM` on
// the Andon tab, and the shop floor's `AndonVM` on the leader's home — which
// has no `owner` object, only an owner's name. `takeTargetOf` narrows the
// second to what the sheet needs: who raised it, and who (if anybody) holds
// it, which is all the verb and the lead sentence read.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { andonApi } from '@/api/andon/client';
import type { AndonAlertVM } from '@/api/andon/types';
import type { AndonVM } from '@/api/types';
import { ApiError } from '@/api/http';
import { useT } from '@/i18n/useT';
import { useStore } from '@/store';
import { EtaSheet } from './EtaSheet';

/** What the ETA sheet needs of an andon, plus the id the take is sent for. */
export type TakeTarget = Pick<AndonAlertVM, 'id' | 'raisedBy' | 'owner'>;

/** The shop floor's andon row, as a take target. */
export function takeTargetOf(a: AndonVM): TakeTarget {
  const name = a.ownerName.trim() || null;
  return {
    id: a.id,
    raisedBy: a.raisedByName,
    owner: { name, role: null, unowned: a.state === 'new' || name == null, uid: null },
  };
}

export function TakeSheet({
  target,
  onClose,
  onOpenThread,
}: {
  /** The andon being taken, or null for no sheet. */
  target: TakeTarget | null;
  onClose: () => void;
  /** After a take meant as a handover: the thread, where the picker is. */
  onOpenThread: (andonId: number) => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const showToast = useStore((s) => s.showToast);
  const showError = useStore((s) => s.showError);

  const take = useMutation({
    mutationFn: (arg: { id: number; minutes: number | null; basis: 'chosen' | 'end_of_shift' }) => andonApi.respond(arg.id, arg.minutes, arg.basis),
    // The TOAST reads the number off the row that came back, not the one that
    // was sent. On "after my shift" this client sent null and Odoo resolved
    // the minutes against the work centre's calendar, so echoing the request
    // would tell the responder they had promised nothing.
    onSuccess: (a) => {
      const first = a.raisedBy.split(' ')[0];
      showToast(a.etaMinutes == null ? `${t.toastTakenOnT} · ${first} ${t.toastToldT}` : `${t.toastTakenOnT} · ETA ${a.etaMinutes} ${t.minT} ${t.toastSentToT} ${first}`);
      onClose();
      // Every list this andon is on, this app's own included.
      void qc.invalidateQueries({ queryKey: ['andons'] });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : t.alertDidNotGoThroughT),
  });

  if (!target) return null;
  return (
    <EtaSheet
      alert={target}
      open
      onClose={onClose}
      busy={take.isPending}
      onSubmit={(minutes, basis) => take.mutate({ id: target.id, minutes, basis })}
      onHandover={(minutes, basis) => take.mutate({ id: target.id, minutes, basis }, { onSuccess: () => onOpenThread(target.id) })}
    />
  );
}
